import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

import agent from 'agent'
import Icon from 'components/Icon'
import useAuth from 'hooks/use-auth'
import { useTranslation } from 'hooks/use-translation'
import toast from 'lib/toast'
import { TeamExternalIntegration } from 'types/api/status-checks/status-check'
import { messageFromResponseError } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

const DeleteTeamExternalIntegration: React.FC<{
  integration: Pick<
    TeamExternalIntegration,
    'name' | 'type' | 'domain' | 'observabilityToolType'
  >
  externalIntegrationType?: 'STATUS_CHECK' | 'LOAD_GENERATOR'
  onChanged: () => void
  buttonProps?: ButtonProps
}> = ({ buttonProps, integration, externalIntegrationType, onChanged }) => {
  const { trans } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useBoolean(false)
  const { currentTeamId } = useAuth()

  const deleteIntegration = async () => {
    // Special case for Datadog US1 integration. The domain is not required for this integration type.
    const domainName =
      integration.type === IntegrationType.Datadog ||
      integration.domain === null
        ? ''
        : integration.domain

    // The "domain" query param cannot be null or undefined. The backend API will attempt to parse that value into a string
    // and the API call will fail.
    const apiUrl =
      externalIntegrationType === 'STATUS_CHECK'
        ? `external-integrations/status-check?teamId=${currentTeamId}&observabilityToolType=${integration.observabilityToolType}&domain=${domainName}&name=${integration.name}`
        : `external-integrations/load-generator?teamId=${currentTeamId}&type=${integration.type?.toUpperCase()}&name=${
            integration.name
          }`

    try {
      await agent.delete(apiUrl)

      // Close modal prior to calling onChanged to prevent state changes on an unmounted component
      setIsModalOpen(false)
      onChanged()
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was a problem while removing the team integration. Please try again.'
        )
      })

      setIsModalOpen(false)
    }
  }

  return (
    <Box>
      <Button
        color="error"
        data-testid={`team-integration-${integration?.name}-delete`}
        size="small"
        startIcon={<Icon icon="trash" />}
        variant="contained"
        onClick={() => setIsModalOpen(true)}
        {...buttonProps}
      >
        {trans('revoke')}
      </Button>
      <Dialog
        aria-labelledby="alert-dialog-title"
        data-testid="delete-team-integration-dialog"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle color="error" id="alert-dialog-title">
          {trans('status_checks.authentication.revoke.dialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {externalIntegrationType === 'STATUS_CHECK'
              ? trans(
                  'rbac_settings.teams.delete_team_external_integration.dialog_content.text.delete_status_check.warning'
                )
              : trans(
                  'rbac_settings.teams.delete_team_external_integration.dialog_content.text.delete_integration.warning'
                )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="delete-team-integration-dialog-cancel"
            onClick={() => setIsModalOpen(false)}
          >
            {trans('cancel')}
          </Button>
          <Button
            autoFocus
            data-testid="delete-team-integration-dialog-submit"
            onClick={deleteIntegration}
          >
            {trans('revoke')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DeleteTeamExternalIntegration
